"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.EMPTY_ROLLUP = exports.AgreementRollupSchema = exports.DeviceFinanceAgreementSchema = exports.stateSortKey = exports.deriveAgreementState = exports.agreementIdFor = exports.AGREEMENT_STATES = void 0;
const zod_1 = require("zod");
/**
 * DEVICE AGREEMENTS LEDGER — the durable projection of every device finance,
 * lease and rental (spec 2026-08-30).
 *
 * Replaces a `GET /financing` that re-derived the entire book from Stripe on
 * every page load: it listed every subscription on the account and walked
 * every paid invoice of each, which could not survive the book growing.
 *
 * STRIPE REMAINS THE SOURCE OF TRUTH. Every field here is either copied from
 * Stripe or derived from it, the table is rebuildable from Stripe at any
 * moment, and a nightly reconcile re-runs the original derivation to repair
 * drift. Nothing in this schema may become a fact that exists only here.
 */
exports.AGREEMENT_STATES = ['active', 'ending', 'closed'];
/**
 * The row key. Deterministic on purpose: a Stripe webhook knows a
 * subscription and a price (hence a SKU) and must be able to address the row
 * with no lookup, and a retried seed must collide with itself rather than
 * mint a second agreement.
 */
const agreementIdFor = (subscriptionId, skuId) => `${subscriptionId}::${skuId}`;
exports.agreementIdFor = agreementIdFor;
/**
 * The lifecycle bucket, derived — never set by hand, never trusted off the
 * wire.
 *
 * `past_due` and `unpaid` stay ACTIVE deliberately. They are attention
 * conditions on a live agreement, not lifecycle states; giving them their own
 * bucket would hide unpaid money from the default view, which is the one view
 * an operator actually looks at.
 */
const deriveAgreementState = (input) => {
    if (input.subscriptionStatus === 'canceled')
        return 'closed';
    if (input.cancelAtPeriodEnd)
        return 'ending';
    return 'active';
};
exports.deriveAgreementState = deriveAgreementState;
/**
 * GSI1 range key: owing-most-first ordering, straight from the index.
 *
 * Zero-padded to 12 digits because DynamoDB sorts range keys as STRINGS —
 * unpadded, "9" would sort above "1200000". Twelve digits holds $9,999,999,999.99,
 * which no single device agreement will reach.
 *
 * A null remaining (an open-ended rental, which has no balance by definition)
 * sorts BELOW every real balance, so a page of the book leads with money owed
 * rather than with rentals that can never owe anything.
 */
const stateSortKey = (remainingCents, agreementId) => {
    const n = remainingCents === null ? -1 : Math.max(0, Math.round(remainingCents));
    const padded = n < 0 ? '-00000000001' : String(n).padStart(12, '0');
    return `${padded}#${agreementId}`;
};
exports.stateSortKey = stateSortKey;
exports.DeviceFinanceAgreementSchema = zod_1.z.object({
    agreementId: zod_1.z.string().min(1),
    subscriptionId: zod_1.z.string().min(1),
    skuId: zod_1.z.string().min(1),
    /** From `subscription.metadata.tenantId`. Null is REPORTED, never dropped —
     *  an unattributed financed device is the row an operator most needs. */
    tenantId: zod_1.z.string().nullable(),
    tenantName: zod_1.z.string().nullable(),
    name: zod_1.z.string(),
    financeType: zod_1.z.enum(['installment', 'lease', 'rental']),
    rentalKind: zod_1.z.enum(['open_ended', 'fixed_term']).nullable(),
    /** Null for an open-ended rental: it bills until cancelled. */
    termMonths: zod_1.z.number().int().nonnegative().nullable(),
    quantity: zod_1.z.number().int().positive(),
    monthlyCents: zod_1.z.number().int().nonnegative(),
    /** Null wherever termMonths is null — no term means no total. */
    totalCents: zod_1.z.number().int().nonnegative().nullable(),
    paidCents: zod_1.z.number().int().nonnegative(),
    monthsPaid: zod_1.z.number().int().nonnegative(),
    remainingCents: zod_1.z.number().int().nonnegative().nullable(),
    depositCents: zod_1.z.number().int().nonnegative(),
    minCommitmentMonths: zod_1.z.number().int().nonnegative(),
    /** Raw Stripe status, preserved verbatim so the UI chip keeps its meaning. */
    subscriptionStatus: zod_1.z.string(),
    cancelAtPeriodEnd: zod_1.z.boolean(),
    startedAt: zod_1.z.number().int().nullable(),
    currentPeriodEndAt: zod_1.z.number().int().nullable(),
    state: zod_1.z.enum(exports.AGREEMENT_STATES),
    /**
     * Landed cost SNAPSHOTTED at sale time, not joined live off the model.
     * Payback means "rent collected versus what this device cost us WHEN WE SOLD
     * IT" — the truthful comparison, and it removes the class of rows that
     * showed no payback merely because their SKU no longer resolved.
     * Null means genuinely unknown; render no payback, never a guessed one.
     */
    costCents: zod_1.z.number().int().positive().nullable(),
    /**
     * The idempotency set behind `monthsPaid`, which is exactly its length.
     * Stripe WILL redeliver `invoice.paid`; because a month is counted by
     * invoice id rather than by increment, a redelivery is arithmetically
     * incapable of counting one twice.
     */
    paidInvoiceIds: zod_1.z.array(zod_1.z.string()).default([]),
    /**
     * A derived, LOWERCASED haystack — `tenantName` + `name` + `tenantId`,
     * space-joined, nullish parts skipped — that exists solely to make the
     * operator search box case-insensitive against a DynamoDB `contains()`
     * filter, which itself has no case-insensitive mode. STORAGE ONLY: never
     * returned from the API (the down-map in `handlers/financing.ts` excludes
     * it), and never read for anything but the `q` filter in
     * `agreementStore.ts`'s `queryAgreements`.
     */
    searchText: zod_1.z.string().default(''),
    updatedAt: zod_1.z.number().int(),
    /** 'seed' | 'webhook:<event.type>' | 'reconcile' — provenance, for triage. */
    updatedBy: zod_1.z.string(),
});
/**
 * The six stat-tile figures, held on the single row `ROLLUP#GLOBAL` and
 * maintained with atomic ADD. Paging the rows alone would still leave the
 * totals needing a full table read; this is what actually makes the Devices
 * screen cost O(1) as the book grows.
 *
 * The reconcile job recomputes it from scratch and overwrites it, so an ADD
 * that was ever missed or doubled self-corrects within a day.
 */
exports.AgreementRollupSchema = zod_1.z.object({
    agreements: zod_1.z.number().int().nonnegative(),
    totalMonthlyCents: zod_1.z.number().int(),
    totalOutstandingCents: zod_1.z.number().int(),
    activeRentals: zod_1.z.number().int(),
    rentalsPastPayback: zod_1.z.number().int(),
    depositsHeldCents: zod_1.z.number().int(),
});
exports.EMPTY_ROLLUP = {
    agreements: 0,
    totalMonthlyCents: 0,
    totalOutstandingCents: 0,
    activeRentals: 0,
    rentalsPastPayback: 0,
    depositsHeldCents: 0,
};
//# sourceMappingURL=deviceFinanceAgreement.js.map